import { FC, useEffect, useRef, useState } from 'react'
import { Alert, Box, Button, Link, Stack, Typography } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import { API_ORIGIN } from '../../constants/const'
import { FONT_WEIGHT } from '../../shared/styles/typography'

const RESTART_URLS = [
  { browser: 'Edge', url: 'edge://restart' },
  { browser: 'Chrome', url: 'chrome://restart' },
]

const COPIED_MS = 2000

/**
 * 「コピー」の行。コピーできたことは、アイコンをチェックに変えて 2 秒だけ示す。
 * ラベルを「コピーしました」に変えると幅が伸び、横の文字が折り返して下の手順が
 * 動く（押そうとした位置が動く）。幅が変わるものは出さない。
 */
const RestartUrlRow: FC<{ browser: string; url: string }> = ({
  browser,
  url,
}) => {
  const [copied, setCopied] = useState(false)
  const timer = useRef<number>()

  useEffect(() => () => window.clearTimeout(timer.current), [])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      window.clearTimeout(timer.current)
      timer.current = window.setTimeout(() => setCopied(false), COPIED_MS)
    } catch {
      // コピーできないときも、画面の文字を入力すれば同じことができる
    }
  }

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography variant="body2">
        {browser}の場合：
        <Box component="span" sx={{ fontWeight: FONT_WEIGHT.semibold }}>
          {url}
        </Box>
      </Typography>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        startIcon={
          copied ? (
            <CheckIcon fontSize="small" />
          ) : (
            <ContentCopyIcon fontSize="small" />
          )
        }
        onClick={copy}
        sx={{ py: 0, minWidth: 0 }}
      >
        コピー
      </Button>
    </Stack>
  )
}

/**
 * cert 環境で API に届かないとき（証明書が送られていない）の案内。
 *
 * Chromium は証明書の選択（「送らない」も含む）をブラウザのプロセスが
 * 終わるまで覚えている。キャンセルしたあとは鍵アイコンの「証明書の選択」
 * も出なくなるので、ブラウザを開き直す edge://restart / chrome://restart
 * で記憶を消してもらう。Web ページから edge:// / chrome:// へは
 * リンクできないため、コピーして貼り付けてもらう。
 *
 * 手順は時系列の 3 つ。開き直すと他の画面も閉じるので、「先に保存する」を
 * 1 に置き、「コピー」より前に読まれるようにしている（店舗は途中の
 * 「コピー」で手を動かし始める）。強調は色や太字でなく順番で出す。
 * 1 行目の文言は Web ガイド C-2 と CRC の Wiki が逐語で引く。3 手順の文言は
 * Web ガイド C-2（content/c.md）と逐語一致させる（test が突き合わせる）。
 */
export const CertRestartGuide: FC = () => (
  <Alert severity="warning">
    <Stack spacing={1.5}>
      <Typography variant="body2">
        ログインに必要な証明書が正しく選択されていません。
        <br />
        ブラウザを開き直して、証明書を選び直してください。
      </Typography>
      <Stack component="ol" spacing={1} sx={{ m: 0, pl: 2.5 }}>
        <Typography component="li" variant="body2">
          ほかの画面で入力中の内容があれば、先に保存してください。開き直すと、ブラウザの画面はすべて閉じます。
        </Typography>
        <Typography component="li" variant="body2">
          アドレスバー（画面上部のURLが表示されている場所）に次の文字を入力し、Enterキーを押してください。
          <Stack spacing={0.5} mt={0.5}>
            {RESTART_URLS.map((row) => (
              <RestartUrlRow key={row.url} {...row} />
            ))}
          </Stack>
        </Typography>
        <Typography component="li" variant="body2">
          証明書の選択画面が出たら、「キャンセル」を押さずに、表示された証明書（多くの場合、末尾が「.medilab.com」）を選んでください。
        </Typography>
      </Stack>
      <Typography variant="body2">
        直らない場合は、
        {/* 青は警告の地の上で薄く読みづらい。地の文と同じ色にし、下線でリンクと示す */}
        <Link
          href={API_ORIGIN}
          target="_blank"
          rel="noreferrer"
          color="inherit"
          underline="always"
        >
          {API_ORIGIN}
        </Link>
        を開き、表示された画面をお問い合わせ先にお伝えください。
      </Typography>
    </Stack>
  </Alert>
)
